/**
 * Shared Tab Styles
 * 
 * Centralized tab styling used across the application.
 * Import these styles into any component that uses MUI Tabs.
 * 
 * Usage:
 *   import { horizontalTabStyles, verticalTabStyles, tabBodyStyle } from '../sharedStyles';
 * 
 *   const useStyles = makeStyles(() => ({
 *       ...horizontalTabStyles,   // spread into your makeStyles
 *       ...verticalTabStyles,     // for vertical navigation tabs
 *   }));
 * 
 *   <div className={classes.tabWrapper}>
 *       <Tabs ...><Tab /><Tab /></Tabs>
 *   </div>
 *   <div className={classes.tabBody}>...</div>
 */

/**
 * Horizontal tab styles (content area tabs)
 * Used in: Request Details, Daily Feed, Admin sub-pages, Reports, etc.
 */
export const horizontalTabStyles = {
    tabWrapper: {
        margin: '0 0 -1px 0',
        '& .MuiTabs-root': {
            minHeight: '40px',
            paddingLeft: '30px',
        },
        '& .MuiTabs-scrollButtons': {
            '&.Mui-disabled': {
                display: 'none',
            },
        },
        '& .MuiTab-root.Mui-selected': {
            background: '#fff !important',
            color: '#267db7 !important',
            fontWeight: 700,
            borderTop: '3px solid #267db7',
            borderLeft: '1px solid #6591b696',
            borderRight: '1px solid #6591b696',
            borderBottom: 'none',
        },
        '& .MuiTab-root': {
            color: '#fff',
            outline: '0px',
            padding: '10px 22px',
            fontSize: '0.78rem',
            minWidth: '170px',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2a6a9e 100%) !important',
            minHeight: '40px',
            alignItems: 'center',
            marginRight: '3px',
            textTransform: 'uppercase',
            fontWeight: 600,
            borderRadius: '8px 8px 0 0',
            transition: 'all 0.2s ease',
            letterSpacing: '0.5px',
            '&:hover': {
                background: 'linear-gradient(135deg, #264d73 0%, #3180b8 100%) !important',
                color: '#fff',
            },
        },
        '& .MuiTabs-indicator': {
            display: 'none',
        },
    },
    tabBody: {
        border: '1px solid #6591b696',
        background: '#fff',
        borderRadius: '10px',
        padding: '20px',
    },
};

/**
 * Vertical tab styles (side navigation)
 * Used in: Admin, Reports, Tracking
 */
export const verticalTabStyles = {
    verticalTabWrapper: {
        margin: '0px 10px 10px 0px',
        padding: '8px',
        height: '100%',
        '& .MuiTab-root': {
            outline: '0px',
            background: '#fff !important',
            color: '#1e3a5f',
            minHeight: '38px',
            marginBottom: '6px',
            padding: '8px 14px',
            fontSize: '0.8rem',
            fontWeight: 600,
            alignItems: 'flex-start',
            textTransform: 'capitalize',
            borderRadius: '6px',
            letterSpacing: '0.3px',
            border: '1px solid #c8d6e5',
            transition: 'all 0.2s ease',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            '&:hover': {
                background: '#edf2f9 !important',
                borderColor: '#267db7',
                color: '#267db7',
            },
        },
        '& .MuiTab-root.Mui-selected': {
            background: '#fff !important',
            color: '#267db7',
            fontWeight: 700,
            border: '1px solid #267db7',
            boxShadow: '0 2px 6px rgba(38,125,183,0.18)',
        },
        '& .MuiTabs-indicator': {
            display: 'none',
        },
    },
};
